import {IOption} from '../../../interfaces/option/IOption';
import {IOptionGroup} from '../../../interfaces/option/IOptionGroup';
import {
    FURNITURE_CELL_BOX,
    FURNITURE_CELL_CORPUS, FURNITURE_CELL_FACADE, FURNITURE_CELL_OTHER,
    GOOD_TYPE_MODULE,
    KIT_CODE_CHEAP,
    KIT_CODE_EXPENSIVE,
    KIT_CODE_MEDIUM,
    LOCAL_STORAGE_PROJECT_NAME
} from '../../../constants';
import {IImportOffer} from '../../../interfaces/api/IImportOffer';
import {IAPIProductItem} from '../../../interfaces/api/IAPIProductItem';
import {IModuleOffers} from '../../../interfaces/catalog/IModuleOffers';
import {i18n} from '../i18n';
import {TKitCode} from '../../../types/catalog/TKitCode';
import {TFurnitureCell} from '../../../types/catalog/TFurnitureCell';
import {TRange} from "../../../types/math/TRange";

const hash_sum = require("hash-sum");

export class CommonHelper {
    public static md5(value: any): string {
        return hash_sum(value);
    }

    public static deepCompare(
        obj1: any,
        obj2: any,
        compareOrder: boolean = true
    ): boolean {
        if (typeof obj1 !== typeof obj2) {
            return false;
        }
        const types: string[] = ["string", "number", "boolean"];
        if (types.includes(typeof obj1) ||
            obj1 === null ||
            obj1 === undefined
        ) {
            return obj2 === obj1;
        }
        const objEntries1: [string, unknown][] = Object.entries(obj1);
        const objEntries2: [string, unknown][] = Object.entries(obj2);

        return this.compareEntries(objEntries1, objEntries2, compareOrder);
    }

    public static compareEntries(
        firstEntries: [string, unknown][],
        secondEntries: [string, unknown][],
        compareOrder: boolean
    ): boolean {
        if (!firstEntries.length && secondEntries.length) {
            return false;
        }
        if (firstEntries.length !== secondEntries.length && compareOrder) {
            return false;
        }
        return firstEntries.every((value, index): boolean => {
            const secondIndex = secondEntries.findIndex(val => val[0] === value[0]);
            if ((compareOrder && index !== secondIndex) || secondIndex === -1) {
                return false;
            }
            const [k1, v1] = value;
            const [k2, v2] = secondEntries[secondIndex];
            if (k1 !== k2) {
                return false;
            }
            if (typeof v1 === "object" || typeof v2 === "object") {
                return this.deepCompare(v1, v2, compareOrder);
            }

            return v1 === v2;
        })
    }

    public static deepCopy(object: any) {
        let copy: any;

        // Handle the 3 simple types, and null or undefined
        if (null == object || "object" != typeof object) return object;

        // Handle Date
        if (object instanceof Date) {
            copy = new Date();
            copy.setTime(object.getTime());
            return copy;
        }

        // Handle Array
        if (object instanceof Array) {
            copy = [];
            for (let i = 0, len = object.length; i < len; i++) {
                copy[i] = this.deepCopy(object[i]);
            }
            return copy;
        }

        // Handle Object
        if (object instanceof Object) {
            copy = {};
            for (let attr in object) {
                if (object.hasOwnProperty(attr)) copy[attr] = this.deepCopy(object[attr]);
            }
            return copy;
        }

        throw new Error('error-CommonHelper-deepCopy');
    }

    public static convertObjectToStr(object: any, strValue?: string): string {
        let index;

        if (!strValue) {
            strValue = '';
        }
        for (index in object) {
            if (object.hasOwnProperty(index)) {
                if (typeof (object[index]) === 'object') {
                    strValue = this.convertObjectToStr(object[index], strValue);
                } else {
                    strValue += '' + object[index];
                }
            }
        }

        return strValue;
    }

    public static base64ToArrayBuffer(binaryString: string) {
        let len = binaryString.length;
        let bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    public static instanceOfIOption(object: any): object is IOption {
        return 'type' in object;
    }

    public static instanceOfIOptionGroup(object: any): object is IOptionGroup {
        return 'options' in object;
    }

    public static getLocalProjectName(
        userUid?: string,
        corpusMaterial?: string,
        topFacade?: string,
        bottomFacade?: string
    ): string {
        let projectName: string;

        projectName = LOCAL_STORAGE_PROJECT_NAME;
        if (userUid) {
            projectName += '_' + userUid;
        }
        if (corpusMaterial) {
            projectName += '_' + corpusMaterial;
        }
        if (topFacade) {
            projectName += '_' + topFacade;
        }
        if (bottomFacade) {
            projectName += '_' + bottomFacade;
        }

        return projectName;
    }

    public static sortObject(object: any): any {
        return Object.keys(object).sort().reduce(function (result: any, key) {
            result[key] = object[key];
            return result;
        }, {});
    }

    public static relativeValue(value: number, min: number, max: number): number | undefined {
        if (isNaN(+value) || isNaN(+min) || isNaN(+max)) {
            return undefined;
        }
        return (value - min) / (max - min);
    }

    public static normalizeRangeValue(value: number, min: number, max: number): number | undefined {
        if (isNaN(+value) || isNaN(+min) || isNaN(+max)) {
            return undefined;
        }
        return (value < min) ? min : ((value > max) ? max : value);
    }

    public static generateOfferItemsHash(
        offer: IImportOffer,
        externalIdName: 'externalGuid' | 'vendorCode',
        apiExternalIdName: 'guid' | 'vendorCode'
    ): string | undefined {
        let offerItem: IAPIProductItem;
        let offerModuleItems: IModuleOffers = {};
        let offerItemId: string | undefined;


        if (offer.items && offer.items.length) {
            for (offerItem of offer.items) {
                offerItemId = offerItem[apiExternalIdName];
                if (!offerItemId || !offerItemId.length) {
                    continue;
                }
                offerItemId = '' + offerItemId;
                if (!offerModuleItems[offerItemId]) {
                    offerModuleItems[offerItemId] = +offerItem.count
                } else {
                    offerModuleItems[offerItemId] += +offerItem.count;
                }
            }
        } else if (offer.type === GOOD_TYPE_MODULE){
            offerItemId = offer[externalIdName];
            if (offerItemId) {
                offerModuleItems[offerItemId] = 1;
            }
        } else {
            return undefined;
        }

        return Object.keys(offerModuleItems).length ? JSON.stringify(this.sortObject(offerModuleItems)) : undefined;
    }

    public static getFormatDate(date: string): string {
        switch (date) {
            case "week":
                return i18n.t('эта неделя');
            default:
                return date;
        }
    }

    public static getKitCodeTitle(kitCode: TKitCode): string {
        switch (kitCode) {
            case KIT_CODE_CHEAP:
                return 'Стандарт';
            case KIT_CODE_MEDIUM:
                return 'Комфорт';
            case KIT_CODE_EXPENSIVE:
                return 'Люкс';
            default:
                return 'Ошибка! не установлено';
        }
    }

    public static getFurnitureCellTitle(cell: TFurnitureCell): string {
        switch (cell) {
            case FURNITURE_CELL_CORPUS:
                return 'Корпус';
            case FURNITURE_CELL_FACADE:
                return 'Фасад';
            case FURNITURE_CELL_BOX:
                return 'Ящик';
            case FURNITURE_CELL_OTHER:
                return 'Другое';
            default:
                return 'Не установлено';
        }
    }

    public static getKitCodeSort(kitCode: TKitCode): number {
        switch (kitCode) {
            case KIT_CODE_CHEAP:
            default:
                return 0;
            case KIT_CODE_MEDIUM:
                return 1;
            case KIT_CODE_EXPENSIVE:
                return 2;
        }
    }

     public static notEmptyString(value?: string) {
        if (value === undefined || value === null) {
            return false;
        }

        return value && value.length > 0;
    }

    public static updateDetailById<T extends Record<string, any> | any[]>(fullObject: T, detailId: number, detailToUpdate: Partial<T>): T {
        if (typeof fullObject === 'object' && fullObject !== null && 'id' in fullObject && fullObject.id === detailId) {
            return { ...fullObject, ...detailToUpdate } as T;
        }

        if (Array.isArray(fullObject)) {
            return fullObject?.map(item =>
                typeof item === 'object' && item !== null
                    ? this.updateDetailById(item, detailId, detailToUpdate)
                    : item
            ) as unknown as T;
        }

        if (typeof fullObject === 'object' && fullObject !== null) {
            const result: Record<string, any> = {};
            for (const [key, value] of Object.entries(fullObject)) {
                result[key] = typeof value === 'object' && value !== null
                    ? this.updateDetailById(value, detailId, detailToUpdate)
                    : value;
            }
            return result as T;
        }

        return fullObject;
    }

    /**
     * Находит и возвращает первый объект в JSON-структуре по полю id
     * @param fullObject - исходный объект или массив
     * @param detailId - искомый id
     * @returns найденный объект типа T или undefined, если не найден
     */
    public static findDetailById<T extends Record<string, any> = Record<string, any>>(
        fullObject: Record<string, any> | any[],
        detailId: number
    ): T | undefined {
        // 1. Проверяем текущий узел
        if (typeof fullObject === 'object' && fullObject !== null && 'id' in fullObject && fullObject.id === detailId) {
            return fullObject as T;
        }

        // 2. Собираем дочерние элементы для обхода
        const children = Array.isArray(fullObject)
            ? fullObject
            : (typeof fullObject === 'object' && fullObject !== null ? Object.values(fullObject) : []);

        // 3. Рекурсивный поиск с ранним выходом (не обходим лишнее)
        for (const child of children) {
            if (typeof child === 'object' && child !== null) {
                const found = this.findDetailById<T>(child, detailId);
                if (found !== undefined) return found;
            }
        }

        // 4. Не найдено
        return undefined;
    }

    public static hasIntersection(target: TRange, ranges: TRange[]): boolean {
        return ranges.some(r => target.min < r.max && target.max > r.min);
    }

    public static findClosest(arr: number[], target: number): number | undefined {
        if (arr.length === 0) {
            return undefined; // Или выбросить ошибку: throw new Error("Массив пуст");
        }

        return arr.reduce((prev, curr) => {
            // Если разница текущего элемента меньше, чем у предыдущего "лучшего",
            // делаем текущий элемент новым "лучшим"
            return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
        });
    }

    public static isSubset(subset: string[], superset: string[]): boolean {
        // Если искомый массив больше, он точно не может быть подмножеством
        if (subset.length > superset.length) return false;

        const supersetSet = new Set(superset);

        // Проверяем, что каждый элемент из subset есть в Set
        return subset.every(item => supersetSet.has(item));
    }

    public static roundIfClose(value: number, step: number = 50, tolerance: number = 0.5): number {
        // Находим ближайшее круглое число, кратное step
        const nearestRound = Math.round(value / step) * step;

        // Вычисляем модуль разницы между исходным числом и ближайшим круглым
        const difference = Math.abs(value - nearestRound);

        // Если разница в пределах допуска, округляем
        if (difference <= tolerance) {
            return nearestRound;
        }

        // Иначе возвращаем исходное значение (можно заменить на Math.round(value), если нужно обычное округление)
        return value;
    }
}